import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listConfigs, createConfig, statusLabel, formatIsoDate } from '../../lib/activityConfigsApi';
import { usePortalT } from '../../i18n/usePortalT';
import {
  PageTitle,
  PageSubtitle,
  Btn,
  BtnRow,
  Card,
  Table,
  Badge,
  ErrorText,
} from '../../styles/adminStyles';

function badgeStatus(status) {
  if (status === 'published') return 'active';
  if (status === 'archived') return 'ended';
  return 'draft';
}

export default function ActivityListPage({ basePath = '/organizer' }) {
  const navigate = useNavigate();
  const { t, tEnum, language } = usePortalT();
  const isAdmin = basePath.startsWith('/admin');
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setConfigs(await listConfigs());
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (sourceType) => {
    setCreating(true);
    setError(null);
    const out = await createConfig({ sourceType, name: '', rawInput: {} });
    setCreating(false);
    if (out.success && out.config) {
      navigate(`${basePath}/${out.config.id}`);
    } else {
      setError(out.error || t('common.createFailed'));
    }
  };

  return (
    <>
      <PageTitle>{isAdmin ? t('activities.listTitleAdmin') : t('activities.listTitle')}</PageTitle>
      <PageSubtitle>
        {t('activities.listSubtitle')}
      </PageSubtitle>

      <BtnRow style={{ marginTop: 0, marginBottom: 20 }}>
        <Btn $primary type="button" disabled={creating} onClick={() => handleCreate('template')}>
          {t('activities.createTemplate')}
        </Btn>
        <Btn type="button" disabled={creating} onClick={() => handleCreate('natural_language')}>
          {t('activities.createNL')}
        </Btn>
      </BtnRow>

      {error ? <ErrorText>{error}</ErrorText> : null}

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <p style={{ padding: 18 }}>{t('common.loading')}</p>
        ) : configs.length === 0 ? (
          <p style={{ padding: 18, color: '#64748b' }}>{t('activities.empty')}</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>{t('activities.colName')}</th>
                <th>{t('activities.colStatus')}</th>
                <th>{t('activities.colType')}</th>
                <th>{t('activities.colStart')}</th>
                <th>{t('activities.colEnd')}</th>
                <th>{t('activities.colUpdated')}</th>
              </tr>
            </thead>
            <tbody>
              {configs.map((c) => (
                <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`${basePath}/${c.id}`)}>
                  <td style={{ fontWeight: 700 }}>{c.name || t('activities.unnamed')}</td>
                  <td><Badge $status={badgeStatus(c.status)}>{statusLabel(c.status, language)}</Badge></td>
                  <td>{c.sourceType === 'natural_language' ? tEnum('activityType', 'natural_language') : tEnum('activityType', 'template')}</td>
                  <td>{c.activityStartDate || '—'}</td>
                  <td>{c.activityEndDate || '—'}</td>
                  <td>{formatIsoDate(c.updatedAt, language)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}
